import { Vector3 } from 'three/webgpu';
import type { GravityRegion } from '../gravity/GravityField';
import type { Entrance } from './entrance';
import type { CaveLayout } from './layout';
import { CaveSdf, type Pull } from './sdf';

/** One end of a cave for its gravity: the entrance, and how its gate stub pulls. */
export interface CaveFieldEnd {
  entrance: Entrance;
  /** The pull of the cave's tube where it meets this gate (the first passage's). */
  pull: Pull;
}

const _v = new Vector3();
const _seg = new Vector3();
const _g = { x: 0, y: 0, z: 0 };

/**
 * A cave's own gravity ("the cave is a void too"). In the interior every wall is a floor:
 * the pull is toward the nearest wall, strongest there and zero in each space's middle
 * (see CaveSdf.gravity). Down an entrance you feel your world, until the stair levels
 * out: over that last stretch the world's pull fades and the tube's own takes over, and
 * both point at the floor you're on. Out in the rock (only reachable by flying) the
 * worlds' field comes back.
 */
export class CaveField implements GravityRegion {
  readonly min = new Vector3();
  readonly max = new Vector3();
  readonly sdf: CaveSdf;
  private readonly local = new Vector3();

  constructor(
    layout: CaveLayout,
    /** Universe position of the layout's origin. */
    readonly origin: Vector3,
    readonly ends: [CaveFieldEnd, CaveFieldEnd],
  ) {
    this.sdf = new CaveSdf(layout.shape);
    const b = this.sdf.bounds(4);
    this.min.set(b.min[0], b.min[1], b.min[2]).add(origin);
    this.max.set(b.max[0], b.max[1], b.max[2]).add(origin);
    for (const e of ends) {
      for (let i = e.entrance.helixStart; i < e.entrance.path.length; i++) {
        const p = e.entrance.path[i];
        const r = e.entrance.radius + 3;
        this.min.min(_v.set(p.x - r, p.y - r, p.z - r));
        this.max.max(_v.set(p.x + r, p.y + r, p.z + r));
      }
    }
  }

  apply(p: Vector3, out: Vector3): number {
    // Down an entrance (before its gate)?
    for (const end of this.ends) {
      const keep = this.entrance(end, p, out);
      if (keep >= 0) return keep;
    }
    // In the interior (or near its walls)?
    this.local.subVectors(p, this.origin);
    const s = this.sdf.sample(this.local.x, this.local.y, this.local.z);
    if (s.owner < 0 || s.phi > 3) {
      out.set(0, 0, 0);
      return 1;
    }
    const inside = 1 - smooth(1, 3, s.phi);
    this.sdf.gravity(this.local.x, this.local.y, this.local.z, _g);
    out.set(_g.x * inside, _g.y * inside, _g.z * inside);
    return 1 - inside;
  }

  /**
   * Gravity down entrance `end`: −1 if p isn't in it (or is past its gate). Otherwise
   * writes the tube's own share into `out` and returns the share of the world's field.
   */
  private entrance(end: CaveFieldEnd, p: Vector3, out: Vector3): number {
    const e = end.entrance;
    const near = nearestOnEntrance(e, p, e.radius + 1.5, _near, e.helixStart);
    if (!near) return -1;
    // The world's share: all of it until the stair levels out, none at the gate.
    const keep = 1 - smooth(e.levelStart, e.path.length - 1, near.at);
    // The tube's own pull: from its axis toward the wall.
    _v.subVectors(p, near.point);
    const u = Math.min(near.d / e.radius, 1);
    const m = near.d > 1e-6 ? (end.pull.s * u ** end.pull.a * (1 - keep)) / near.d : 0;
    out.copy(_v).multiplyScalar(m);
    return keep;
  }
}

export interface EntrancePoint {
  /** Position along the path, in path indices (fractional). */
  at: number;
  /** The nearest point on the centre-line, and the distance to it. */
  point: Vector3;
  d: number;
}
const _near: EntrancePoint = { at: 0, point: new Vector3(), d: 0 };

/**
 * The nearest point on an entrance's centre-line to p, if p is within `maxDist` of it and
 * not past its gate (past it is the interior). Searches the path from index `from` on.
 * Writes into `out` (a shared object by default).
 */
export function nearestOnEntrance(
  e: Entrance,
  p: Vector3,
  maxDist: number,
  out: EntrancePoint = _near,
  from = 0,
): EntrancePoint | null {
  const path = e.path;
  // A coarse pass, then the segments around its best point.
  let best = Number.POSITIVE_INFINITY;
  let i0 = from;
  for (let i = from; i < path.length; i += 4) {
    const d = path[i].distanceToSquared(p);
    if (d < best) {
      best = d;
      i0 = i;
    }
  }
  if (best > (maxDist + 12) ** 2) return null;
  best = Number.POSITIVE_INFINITY;
  let bi = i0;
  let bt = 0;
  for (let i = Math.max(from, i0 - 5); i < Math.min(path.length - 1, i0 + 5); i++) {
    _seg.subVectors(path[i + 1], path[i]);
    _v.subVectors(p, path[i]);
    const t = Math.min(Math.max(_v.dot(_seg) / Math.max(_seg.lengthSq(), 1e-12), 0), 1);
    const d = _v.addScaledVector(_seg, -t).lengthSq();
    if (d < best) {
      best = d;
      bi = i;
      bt = t;
    }
  }
  const d = Math.sqrt(best);
  if (d > maxDist) return null;
  // Nearest to the gate itself, and beyond its plane: that's the interior.
  if (bi === path.length - 2 && bt >= 1 && _v.subVectors(p, e.gate).dot(e.gateDir) > 0) return null;
  out.at = bi + bt;
  out.point.copy(path[bi]).lerp(path[bi + 1], bt);
  out.d = d;
  return out;
}

function smooth(e0: number, e1: number, x: number): number {
  const t = Math.min(Math.max((x - e0) / (e1 - e0), 0), 1);
  return t * t * (3 - 2 * t);
}
